// Archetype starter compositions + legacy-field migration into sandbox blocks.

import {
  defaultIntentSignatureParties,
  normalizeIntentPageLayout,
} from "./proposalIntentLayout.js";
import {
  createCalcSummaryBlock,
  createCoverClosingBlock,
  createCoverLetterheadBlock,
  createCustomerSampleBlock,
  createPaymentMilestonesBlock,
  createRichTextBlock,
  createSignaturePartyBlock,
  findRichTextByRole,
  normalizeSandboxBlocksArray,
  type ProposalSandboxBlock,
  type ProposalSandboxBlockType,
} from "./proposalSandboxBlocks.js";

export function defaultCoverSandboxBlocks(bodyText: string): ProposalSandboxBlock[] {
  return [
    createCoverLetterheadBlock(true),
    createRichTextBlock({ text: bodyText, role: "body", included: true }),
    createCoverClosingBlock(true),
  ];
}

export function defaultCostSandboxBlocks(args: {
  introText: string;
  footerText: string;
}): ProposalSandboxBlock[] {
  return [
    createRichTextBlock({ text: args.introText, role: "intro", included: true }),
    createCalcSummaryBlock(true),
    createRichTextBlock({ text: args.footerText, role: "footer", included: true }),
  ];
}

export function defaultPaymentSandboxBlocks(args: {
  introText: string;
  footerText: string;
}): ProposalSandboxBlock[] {
  return [
    createRichTextBlock({ text: args.introText, role: "intro", included: true }),
    createPaymentMilestonesBlock(true),
    createRichTextBlock({ text: args.footerText, role: "footer", included: true }),
  ];
}

export function defaultIntentSandboxBlocks(args: {
  preambleText: string;
  signatureIntroText: string;
}): ProposalSandboxBlock[] {
  return [
    createRichTextBlock({
      text: args.preambleText,
      role: "preamble",
      included: true,
    }),
    createCustomerSampleBlock(true),
    createRichTextBlock({
      text: args.signatureIntroText,
      role: "signature_intro",
      included: true,
    }),
    ...defaultIntentSignatureParties().map((p) => createSignaturePartyBlock(p)),
  ];
}

/** @deprecated Prefer normalize from starters; kept for intent migrate path. */
export function normalizeProposalSandboxBlocks(raw: {
  blocks?: unknown;
  preambleText?: string;
  preambleIncluded?: boolean;
  signatureIntroText?: string;
  signatureIntroIncluded?: boolean;
  show_customer_sample?: boolean;
  signature_parties?: unknown;
}): ProposalSandboxBlock[] {
  return migrateIntentSandboxBlocks(raw);
}

export function migrateIntentSandboxBlocks(raw: {
  blocks?: unknown;
  preambleText?: string;
  preambleIncluded?: boolean;
  signatureIntroText?: string;
  signatureIntroIncluded?: boolean;
  show_customer_sample?: boolean;
  signature_parties?: unknown;
}): ProposalSandboxBlock[] {
  const fromBlocks = normalizeSandboxBlocksArray(raw.blocks);
  if (fromBlocks.length > 0) return fromBlocks;

  const layout = normalizeIntentPageLayout({
    show_customer_sample: raw.show_customer_sample,
    signature_parties: raw.signature_parties,
  });
  const blocks: ProposalSandboxBlock[] = [
    createRichTextBlock({
      text: raw.preambleText ?? "",
      role: "preamble",
      included: raw.preambleIncluded !== false,
    }),
  ];
  if (layout.show_customer_sample) blocks.push(createCustomerSampleBlock(true));
  blocks.push(
    createRichTextBlock({
      text: raw.signatureIntroText ?? "",
      role: "signature_intro",
      included: raw.signatureIntroIncluded !== false,
    }),
  );
  for (const party of layout.signature_parties) {
    blocks.push(createSignaturePartyBlock(party));
  }
  return blocks;
}

export function migrateCoverSandboxBlocks(raw: {
  blocks?: unknown;
  bodyText?: string;
  bodyIncluded?: boolean;
}): ProposalSandboxBlock[] {
  const fromBlocks = normalizeSandboxBlocksArray(raw.blocks);
  if (fromBlocks.length > 0) return fromBlocks;
  return defaultCoverSandboxBlocks(raw.bodyText ?? "").map((b) =>
    b.type === "rich_text" && b.role === "body"
      ? { ...b, included: raw.bodyIncluded !== false, text: raw.bodyText ?? "" }
      : b,
  );
}

export function migrateCostSandboxBlocks(raw: {
  blocks?: unknown;
  introText?: string;
  introIncluded?: boolean;
  footerText?: string;
  footerIncluded?: boolean;
}): ProposalSandboxBlock[] {
  const fromBlocks = normalizeSandboxBlocksArray(raw.blocks);
  if (fromBlocks.length > 0) return fromBlocks;
  return [
    createRichTextBlock({
      text: raw.introText ?? "",
      role: "intro",
      included: raw.introIncluded !== false,
    }),
    createCalcSummaryBlock(true),
    createRichTextBlock({
      text: raw.footerText ?? "",
      role: "footer",
      included: raw.footerIncluded !== false,
    }),
  ];
}

export function migratePaymentSandboxBlocks(raw: {
  blocks?: unknown;
  introText?: string;
  introIncluded?: boolean;
  footerText?: string;
  footerIncluded?: boolean;
}): ProposalSandboxBlock[] {
  const fromBlocks = normalizeSandboxBlocksArray(raw.blocks);
  if (fromBlocks.length > 0) return fromBlocks;
  return [
    createRichTextBlock({
      text: raw.introText ?? "",
      role: "intro",
      included: raw.introIncluded !== false,
    }),
    createPaymentMilestonesBlock(true),
    createRichTextBlock({
      text: raw.footerText ?? "",
      role: "footer",
      included: raw.footerIncluded !== false,
    }),
  ];
}

export function textPartsFromSandboxBlocks(blocks: readonly ProposalSandboxBlock[]): {
  preamble: { text: string; included: boolean };
  signature_block: { text: string; included: boolean };
} {
  const texts = blocks.filter((b) => b.type === "rich_text");
  const preamble = findRichTextByRole(blocks, "preamble") ?? texts[0] ?? null;
  const signature =
    findRichTextByRole(blocks, "signature_intro") ??
    texts.find((b) => b !== preamble) ??
    null;
  return {
    preamble: {
      text: preamble && preamble.type === "rich_text" ? preamble.text : "",
      included: preamble ? preamble.included !== false : false,
    },
    signature_block: {
      text: signature && signature.type === "rich_text" ? signature.text : "",
      included: signature ? signature.included !== false : false,
    },
  };
}

export function coverPartsFromSandboxBlocks(blocks: readonly ProposalSandboxBlock[]): {
  body: { text: string; included: boolean };
} {
  const body = findRichTextByRole(blocks, "body");
  const letterheadOn = blocks.some(
    (b) => b.type === "cover_letterhead" && b.included !== false,
  );
  const closingOn = blocks.some(
    (b) => b.type === "cover_closing" && b.included !== false,
  );
  const bodyIncluded = body ? body.included !== false : false;
  return {
    body: {
      text: body?.text ?? "",
      // Page shows if body or structural letter parts are included.
      included: bodyIncluded || letterheadOn || closingOn,
    },
  };
}

export function introFooterFromSandboxBlocks(blocks: readonly ProposalSandboxBlock[]): {
  intro: { text: string; included: boolean };
  footer: { text: string; included: boolean };
} {
  const intro = findRichTextByRole(blocks, "intro");
  const footer = findRichTextByRole(blocks, "footer");
  const hasTable = blocks.some(
    (b) =>
      (b.type === "calc_summary" || b.type === "payment_milestones") &&
      b.included !== false,
  );
  return {
    intro: {
      text: intro?.text ?? "",
      included: intro ? intro.included !== false : hasTable,
    },
    footer: {
      text: footer?.text ?? "",
      included: footer ? footer.included !== false : true,
    },
  };
}

export function addableSandboxBlocksForArchetype(
  archetype: "cover_letter" | "cost_breakdown" | "payment_schedule" | "intent_to_purchase",
): ProposalSandboxBlockType[] {
  switch (archetype) {
    case "cover_letter":
      return ["rich_text", "cover_letterhead", "cover_closing"];
    case "cost_breakdown":
      return ["rich_text", "calc_summary", "fill_field"];
    case "payment_schedule":
      return ["rich_text", "payment_milestones", "fill_field"];
    case "intent_to_purchase":
      return ["rich_text", "customer_sample", "signature_party", "fill_field"];
  }
}
